import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {
  FillStructuredTableRunStatus,
  FillStructuredTableRunStatusEnum,
  FillStructuredTablePartialResponse,
  FillStructuredTableStatusEnum,
} from 'notebook/ai-tools/NotebookAIToolsDAO';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {getSum} from 'util/numbers/getSum';

interface ReviewAndConfirmResultsLoadingProps {
  fillStructuredTableStatus: FillStructuredTablePartialResponse | null;
}

export default function ReviewAndConfirmResultsLoading({
  fillStructuredTableStatus,
}: ReviewAndConfirmResultsLoadingProps): JSX.Element {
  const overallStatusMessage = getStatusMessage(fillStructuredTableStatus?.status ?? null);
  const modelStatusMessage = getModelStatusMessage(fillStructuredTableStatus?.runs ?? []);

  return (
    <div className={css(styles.loadingMessage)}>
      <div>
        {overallStatusMessage}
        {modelStatusMessage && ` ${modelStatusMessage}`}
      </div>
      <HorizontalThrobber />
    </div>
  );
}

function getStatusMessage(status: FillStructuredTableStatusEnum | null): string {
  switch (status) {
    case FillStructuredTableStatusEnum.QUEUED:
    case FillStructuredTableStatusEnum.PROCESSING_FILES:
      return 'Processing input files. This may take a moment.';
    case FillStructuredTableStatusEnum.IN_PROGRESS:
      return 'AI models are analyzing your data. This may take several minutes.';
    default:
      return 'Processing data to fill the table. This may take several minutes.';
  }
}

/**
 * Calculates a progress indicator based on model statuses.
 * Strategy: Each model goes through 2 steps (generating=1, completed/failed=2),
 * plus 1 initial step. So max progress is (number of models * 2 + 1).
 * Current progress is sum of completed steps + 1.
 */
function getModelStatusMessage(modelStatuses: Array<FillStructuredTableRunStatus>): string | null {
  if (modelStatuses.length === 0) {
    return null;
  }

  const maximumProgress = modelStatuses.length * 2 + 1;

  const currentProgress =
    getSum(
      modelStatuses.map((currentModelStatus) => {
        switch (currentModelStatus.currentStatus) {
          case FillStructuredTableRunStatusEnum.GENERATING_ROWS:
            return 1;
          case FillStructuredTableRunStatusEnum.COMPLETED:
          case FillStructuredTableRunStatusEnum.FAILED:
            return 2;
          default:
            return 0;
        }
      })
    ) + 1;

  return `Step ${currentProgress} of ${maximumProgress}.`;
}

const styles = StyleSheet.create({
  loadingMessage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    width: '100%',
    padding: '16px 0',
  },
});
